import { computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import type { License } from '@/types/admin';

export type LicenseBlockReason = 'missing' | 'expired' | 'suspended' | 'inactive' | null;

export function evaluateLicenseStatus(license?: License | null): {
  isValid: boolean;
  reason: LicenseBlockReason;
} {
  if (!license) {
    return { isValid: false, reason: 'missing' };
  }
  if (license.status === 'suspended') {
    return { isValid: false, reason: 'suspended' };
  }
  if (license.status === 'expired') {
    return { isValid: false, reason: 'expired' };
  }
  const expiryDate = new Date(license.expiry_date);
  if (Number.isNaN(expiryDate.getTime()) || expiryDate <= new Date()) {
    return { isValid: false, reason: 'expired' };
  }
  if (license.status !== 'active') {
    return { isValid: false, reason: 'inactive' };
  }
  return { isValid: true, reason: null };
}

export function isLicenseExemptPath(path: string): boolean {
  return path === '/profile';
}

export function useLicenseStatus() {
  const auth = useAuthStore();

  const licenseState = computed(() => {
    if (auth.user?.role === 'superadmin') {
      return { isValid: true, reason: null as LicenseBlockReason };
    }
    return evaluateLicenseStatus(auth.user?.license);
  });

  const isLicenseValid = computed(() => licenseState.value.isValid);
  const licenseBlockReason = computed(() => licenseState.value.reason);

  return {
    isLicenseValid,
    licenseBlockReason,
    evaluateLicenseStatus,
  };
}
